import { z } from "zod";
import { AnswerFinderSourceViewSchema } from "../../../../skills/leadcue-reply/scripts/lib/answer-finder-contracts.ts";
import { EarlierConversationSchema } from "../../../../skills/leadcue-reply/scripts/lib/earlier-conversation.ts";
import { WorkPlanSchema } from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { StoredConversationEventSchema } from "../../src/conversation-events.ts";
import {
  ForcedDraftRecordSchema,
  PlainRedraftRecordSchema,
  RedraftLiftRecordSchema,
} from "../../src/draft-force-records.ts";
import { OfferSourceLineSchema } from "../../src/draft-source-line.ts";
import { InFlightRunSchema } from "../../src/in-flight-view.ts";
import { LateReplyViewSchema } from "../../src/late-reply-view.ts";
import { DraftRevisionSchema, ThreadSnapshotSchema } from "../../src/mail-content.ts";
import { RepReplyActivitySchema } from "../../src/rep-reply.ts";
import { RefusedSpanSchema } from "../../src/withheld-view.ts";
import { DraftingDecisionSchema } from "../drafting-switches.tsx";

export const DetailSchema = z.object({
  plan: WorkPlanSchema,
  locked: z.boolean(),
  drafts: z.array(DraftRevisionSchema),
  thread: ThreadSnapshotSchema.nullable(),
  drafting: DraftingDecisionSchema.default(null),
  /** Read-only: whether this workspace may send an approved reply itself. Off unless granted. */
  sendOnApprove: z.boolean().default(false),
  /** Read-only: whether this workspace holds a HubSpot connection a booking can be checked against. */
  hubSpotConnected: z.boolean().default(false),
  answerFinderSources: z.array(AnswerFinderSourceViewSchema).default([]),
  /** What each setting would add to this draft, from this draft's own mailbox. */
  signOffOptions: z
    .object({
      first_name: z.string().nullable(),
      none: z.string().nullable(),
      full_signature: z.string().nullable(),
    })
    .nullable()
    .default(null),
  /** Set while the lead's newest message is one the app held during campaign setup. */
  lateReply: LateReplyViewSchema.nullable().default(null),
  /** The rep's newest answer that nothing has followed; absent from a server without it. */
  inFlight: InFlightRunSchema.nullable().default(null),
  /** A redraft on its way, where Draft again is on: the first paint, before the offer is read. */
  redraftSince: z.string().nullable().default(null),
  /** Where each generated draft's offer came from; empty from a server without them. */
  offerSources: z.array(OfferSourceLineSchema).default([]),
  /* Questions the draft pointed to the call (w156). Absent means none; rows are read one by one. */
  deferredQuestions: z.array(z.unknown()).optional(),
  /* Notes on a draft that ask nothing of the rep (w198). Absent means none; read row by row. */
  draftNotes: z.array(z.unknown()).optional(),
  /** A reply to a follow-up campaign: the lead's earlier conversation, as drafting read it. */
  earlierConversation: EarlierConversationSchema.nullable().default(null),
  /** The Done that closed the agent's work, while it is the plan's outcome (LEA-225); null else. */
  doneClosure: z.object({ doneAt: z.string(), doneBy: z.string() }).nullable().default(null),
});

/*
 * `forcedDrafts` defaults to an empty array so a browser that has this build, talking to a server
 * that does not yet, keeps working: the records live in their own table and their own field
 * (migration 046), never inside the event vocabulary.
 */
export const EventsSchema = z.object({
  events: z.array(StoredConversationEventSchema),
  forcedDrafts: z.array(ForcedDraftRecordSchema).default([]),
  redraftLifts: z.array(RedraftLiftRecordSchema).default([]),
  /** Redrafts that lifted nothing; absent from a server that predates the field. */
  plainRedrafts: z.array(PlainRedraftRecordSchema).default([]),
  repReplies: z.array(RepReplyActivitySchema).default([]),
  /** What each check refused in a withheld draft, quoted for this workspace (`refused-spans.ts`). */
  refusedSpans: z.array(RefusedSpanSchema).default([]),
  /** The reviewed outcomes a Done made (LEA-225); empty from a server without them. */
  doneDecisions: z.array(z.string()).default([]),
});
